import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send, Loader2, Briefcase, LifeBuoy, Receipt, Headset } from 'lucide-react';
import { RevealContact } from '../components/RevealContact';
import { RAQ_CONTACTS } from '../lib/contacts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactSchema = z.object({
  name: z.string().min(2, 'Nom requis'),
  email: z.string().email('Email invalide'),
  phone: z.string().optional(),
  company: z.string().optional(),
  subject: z.string().min(2, 'Sujet requis'),
  message: z.string().min(10, 'Message trop court'),
});

export default function Contact() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(contactSchema)
  });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await axios.post(`${API}/contact`, data);
      toast.success(t('contact.success'));
      reset();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      {/* Header */}
      <section className="relative py-20 overflow-hidden bg-gray-50">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1758833502047-8f1c7dc5edd7?w=1920"
            alt="Contact"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-black">{t('contact.title')}</h1>
            </div>
            <p className="text-gray-600 text-lg">{t('contact.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="space-y-4">
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="p-6 space-y-3">
                  <div className="mb-2">
                    <p className="text-black font-semibold mb-1">Nos coordonnées</p>
                    <p className="text-gray-500 text-xs">Cliquez pour afficher chaque coordonnée</p>
                  </div>
                  <RevealContact type="phone" label="Téléphone" value={RAQ_CONTACTS.phone} testId="contact-phone-btn" />
                  <RevealContact type="email" label="Ventes" value={RAQ_CONTACTS.emails.sales} icon={Briefcase} testId="contact-sales-btn" />
                  <RevealContact type="email" label="Support technique" value={RAQ_CONTACTS.emails.support} icon={LifeBuoy} testId="contact-support-btn" />
                  <RevealContact type="email" label="Comptabilité" value={RAQ_CONTACTS.emails.accounting} icon={Receipt} testId="contact-accounting-btn" />
                  <RevealContact type="email" label="Service à la clientèle" value={RAQ_CONTACTS.emails.customerService} icon={Headset} testId="contact-customer-service-btn" />
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center">
                      <Clock className="h-6 w-6 text-black" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Disponibilité</p>
                      <p className="text-lg font-semibold text-black">24/7/365</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-black" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Siège social</p>
                      <p className="text-lg font-semibold text-black">Québec, Canada</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-700">{t('contact.name')} *</Label>
                        <Input
                          id="name"
                          {...register('name')}
                          className="bg-white border-gray-300 text-black placeholder:text-gray-400"
                          data-testid="contact-name"
                        />
                        {errors.name && (
                          <p className="text-red-600 text-sm">{errors.name.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-gray-700">{t('contact.email')} *</Label>
                        <Input
                          id="email"
                          type="email"
                          {...register('email')}
                          className="bg-white border-gray-300 text-black placeholder:text-gray-400"
                          data-testid="contact-email"
                        />
                        {errors.email && (
                          <p className="text-red-600 text-sm">{errors.email.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-gray-700">{t('contact.phone')}</Label>
                        <Input
                          id="phone"
                          {...register('phone')}
                          className="bg-white border-gray-300 text-black placeholder:text-gray-400"
                          data-testid="contact-phone"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-gray-700">{t('contact.company')}</Label>
                        <Input
                          id="company"
                          {...register('company')}
                          className="bg-white border-gray-300 text-black placeholder:text-gray-400"
                          data-testid="contact-company"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-gray-700">{t('contact.subject')} *</Label>
                      <Input
                        id="subject"
                        {...register('subject')}
                        className="bg-white border-gray-300 text-black placeholder:text-gray-400"
                        data-testid="contact-subject"
                      />
                      {errors.subject && (
                        <p className="text-red-600 text-sm">{errors.subject.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-gray-700">{t('contact.message')} *</Label>
                      <Textarea
                        id="message"
                        {...register('message')}
                        rows={5}
                        className="bg-white border-gray-300 text-black placeholder:text-gray-400"
                        data-testid="contact-message"
                      />
                      {errors.message && (
                        <p className="text-red-600 text-sm">{errors.message.message}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto bg-black text-white hover:bg-gray-800"
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      {t('contact.send')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
